import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

export interface IdpDemographicsReviewDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ReviewItem {
  readonly label: string;
  readonly detail: string;
}

// Each entry corresponds to a specific value Experian matches against the
// clinician's credit profile. The wording targets the most common typos and
// misunderstandings (debit vs. credit card, office vs. home address, etc.) we
// see drive IDP failures in production.
const REVIEW_ITEMS: ReadonlyArray<ReviewItem> = [
  {
    label: 'Legal name',
    detail:
      'Confirm your first and last name match the name on your credit card statement exactly, including any suffix (Jr., III) Experian may have on file.',
  },
  {
    label: 'Home address',
    detail:
      "Use the billing address from your credit card statement, not your office or practice. Apartment / unit numbers must match the statement's format.",
  },
  {
    label: 'Date of birth',
    detail:
      'Enter as MM/DD/YYYY. A wrong year (or a transposed month and day) is one of the most common causes of failures.',
  },
  {
    label: 'Social Security Number',
    detail: 'Enter all 9 digits with no dashes or spaces.',
  },
  {
    label: 'Credit card number',
    detail:
      'Use a personal credit card — debit, prepaid, gift, and HSA/FSA cards are not accepted by Experian. Enter all 16 digits with no spaces.',
  },
  {
    label: 'Mobile phone number',
    detail:
      'Use a personal mobile number that can receive SMS. Office lines, landlines, and most VOIP numbers will be rejected.',
  },
];

export function IdpDemographicsReviewDialog({ open, onClose }: IdpDemographicsReviewDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-yellow-600" />
            <DialogTitle>Identity verification didn&apos;t go through</DialogTitle>
          </div>
          <DialogDescription>
            Experian couldn&apos;t match the information you submitted. Please carefully review each
            of the items below before trying again — even small typos cause failures, and Experian
            limits the number of attempts you can make.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-3">
          {REVIEW_ITEMS.map((item) => (
            <li key={item.label} className="flex items-start gap-2">
              <CheckCircle2 className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <div className="text-sm">
                <span className="font-medium">{item.label}.</span>{' '}
                <span className="text-muted-foreground">{item.detail}</span>
              </div>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button type="button" onClick={onClose} radius="pill">
            Review my information
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
